import { UserFlowType } from "../types/types";
import { encodeCallbackState } from "./scanner-callback-state";

const scannerFlowId: Record<UserFlowType.Solo | UserFlowType.Assisted | UserFlowType.AssistedAdult, string> = {
  [UserFlowType.Solo]: "solo",
  [UserFlowType.Assisted]: "assisted",
  [UserFlowType.AssistedAdult]: "assisted-accuracy",
};

interface BuildScannerUrlOpts {
  clientId: string;
  deviceId: string;
  currentUrl: string;
  previousPurchaseId?: string;
  session?: string;
  paperFormat?: string;
  skipValidation?: boolean;
  language?: string;
  integrationId?: string;
  journeyId?: string;
  email?: string;
}

export function stripScanRoundtripParams(url: string): URL {
  const clean = new URL(url);
  clean.searchParams.delete("callback_state");
  clean.searchParams.delete("scanId");
  clean.searchParams.delete("scan_id");
  clean.searchParams.delete("state");
  clean.searchParams.delete("load_from_qr");
  return clean;
}

function buildScannerUrl(
  flow: UserFlowType.Solo | UserFlowType.Assisted | UserFlowType.AssistedAdult,
  opts: BuildScannerUrlOpts
): URL {
  const url = new URL(`https://${getScannerHost()}/`);
  url.searchParams.set("flow", scannerFlowId[flow]);
  url.searchParams.set("clientId", opts.clientId);
  url.searchParams.set("deviceId", opts.deviceId);

  if (opts.session) url.searchParams.set("visaSession", opts.session);
  if (opts.paperFormat) url.searchParams.set("paperFormat", opts.paperFormat);
  if (opts.skipValidation) url.searchParams.set("skipValidation", "true");
  if (opts.language) url.searchParams.set("lng", opts.language);
  if (opts.integrationId) url.searchParams.set("integrationId", opts.integrationId);
  if (opts.journeyId) url.searchParams.set("journeyId", opts.journeyId);

  return url;
}

export function getScannerUrl(
  flow: UserFlowType.Solo | UserFlowType.Assisted | UserFlowType.AssistedAdult,
  opts: BuildScannerUrlOpts
): string {
  return buildScannerUrl(flow, opts).toString();
}

export function getScannerUrlWithCallbacks(
  flow: UserFlowType.Solo | UserFlowType.Assisted | UserFlowType.AssistedAdult,
  opts: BuildScannerUrlOpts
): string {
  const url = buildScannerUrl(flow, opts);
  const base = stripScanRoundtripParams(opts.currentUrl);

  const callbackUrl = new URL(base.href);
  callbackUrl.searchParams.set("callback_state", "result");
  const abortUrl = new URL(base.href);
  abortUrl.searchParams.set("callback_state", "abort");

  url.searchParams.set("callbackUrl", callbackUrl.toString());
  url.searchParams.set("abortUrl", abortUrl.toString());
  url.searchParams.set(
    "state",
    encodeCallbackState({
      deviceId: opts.deviceId,
      previousPurchaseId: opts.previousPurchaseId,
      session: opts.session,
      email: opts.email,
    })
  );

  return url.toString();
}

function getScannerHost(): string {
  const { DEV, VITE_ENVIRONMENT, VITE_SCANNER_HOST } = import.meta.env;
  if (typeof VITE_SCANNER_HOST === "string" && VITE_SCANNER_HOST.length > 0) return VITE_SCANNER_HOST;
  return DEV || VITE_ENVIRONMENT === "staging" ? "scan.volumental.dev" : "scan.volumental.com";
}
